import { useState } from 'react';
import classNames from 'classnames';
import { IconEditSliders } from '@medifind/icons';
import { Button } from '@medifind/shared-basic-components';
import { useClinicalTrialStepper } from '@medifind/zustand';
import PersonalizeFooter from './PersonalizeFooter';
import Step from './Step';
import styles from './StepShared.module.scss';

const InitialQuestion = ({ onOptionChange, disableOptions }) => {
  return (
    <div className={styles['btn-row']}>
      <Button
        className={classNames(styles['btn'], { [styles['btn--disabled']]: disableOptions })}
        disabled={disableOptions}
        onClick={() => onOptionChange({ display: 'Yes', value: 'Y' })}
        type="normal"
        label="Yes, Filter Results"
      />
      <Button
        className={classNames(styles['btn'], { [styles['btn--disabled']]: disableOptions })}
        disabled={disableOptions}
        onClick={() => onOptionChange({ display: 'No', value: 'N' })}
        type="normal"
        label="No, See All Results"
      />
    </div>
  );
};

const QuestionRow = ({ onOptionChange, disableOptions, noQuestions }) => {
  const handleChange = ({ event, selection }) => {
    event.target.blur();
    onOptionChange(selection);
  };

  return (
    <div className={styles['btn-row']}>
      {!noQuestions && (
        <>
          <Button
            className={classNames(styles['btn'], { [styles['btn--disabled']]: disableOptions })}
            disabled={disableOptions}
            onClick={(event) => handleChange({ event, selection: { display: 'Yes', value: 'yes' } })}
            type="normal"
            label="Yes"
          />
          <Button
            className={classNames(styles['btn'], { [styles['btn--disabled']]: disableOptions })}
            disabled={disableOptions}
            onClick={(event) => handleChange({ event, selection: { display: 'No', value: 'no' } })}
            type="normal"
            label="No"
          />
          <Button
            className={classNames(styles['btn'], { [styles['btn--disabled']]: disableOptions })}
            disabled={disableOptions}
            onClick={(event) => handleChange({ event, selection: { display: 'Unsure', value: 'unsure' } })}
            type="normal"
            label="Not Sure"
          />
        </>
      )}
    </div>
  );
};

const PersonalizeStep = ({ handleStepSubmit, stepKey, trialCount, showOptional, nextQuestion, loading, isBackwards }) => {
  const steps = useClinicalTrialStepper((state) => state.steps);
  const [showQuestions, setShowQuestions] = useState(false);
  const personalizeResultsStep = steps.find(({ key }) => key === stepKey);
  const { data } = personalizeResultsStep;
  const answeredQuestionsLength = data?.value?.length;
  const questionsAnswered = answeredQuestionsLength > 0;
  const questionNumber = answeredQuestionsLength + 1;
  const displayQuestions = showQuestions || questionsAnswered;
  const currentQuestion = isBackwards && questionsAnswered ? data.value[answeredQuestionsLength - 1] : nextQuestion;
  const { question, conceptId } = currentQuestion || {};

  const handleInitialQuestion = ({ value }) => {
    if (value === 'N') {
      return handleStepSubmit({ data: { value: [] } });
    }
    handleStepSubmit({ data: { value: [] }, doNotAdvance: true });
    return setShowQuestions(true);
  };

  const handleAnswerQuestion = ({ value }) => {
    const lastQuestion = data.value[0] || {};
    const { conceptId: lastConceptId } = lastQuestion;
    const { affectedCount, totalResults } = nextQuestion || {};
    const percentageAffected = affectedCount / totalResults;

    const doNotMoveForward = () => {
      if (isBackwards) return true;
      if (nextQuestion === undefined) return true;
      if (percentageAffected === 0) return true;
      // if more than 5 questions have been answered and the next question will only affect less than 5 percent, skip to next step
      if (answeredQuestionsLength >= 5 && percentageAffected <= 0.05) return true;
      // return changeURLParams(router, { step: queryStep + 1 });
      return false;
    };
    // prevent duplicated answered questions for accidental double clicks
    if (lastConceptId !== conceptId) {
      const newValue = [...data.value, { conceptId, answer: value }];
      return handleStepSubmit({ data: { value: newValue }, doNotAdvance: newValue.length < 8 || doNotMoveForward() });
    }
  };

  const handleGoBack = () => {
    // remove last answered question instead of previous step
    const newValue = data.value.slice(0, -1);
    return handleStepSubmit({ data: { value: newValue }, doNotAdvance: true });
  };

  const questionTest = question
    ? `Question #${questionNumber}: ${question}`
    : 'Unfortunately there are no relevant questions. Please move forward.';
  return (
    <Step
      title="Personalize Your Results"
      subTitle={
        displayQuestions ? questionTest : "Answer a few more questions and let MediFind's AI technology further narrow down your results."
      }
      icon={<IconEditSliders />}
      footer={<PersonalizeFooter />}
      optionsColStyles={styles['personalize-step']}
      trialCount={trialCount}
      showTrialCount
      showOptional={showOptional}
      prevButtonActionOverride={questionsAnswered ? handleGoBack : null}
      // nextButtonActionOverride={displayQuestions && 'View Trials'}
      loading={loading}
    >
      {displayQuestions ? (
        <QuestionRow onOptionChange={handleAnswerQuestion} noQuestions={!question} disableOptions={!trialCount || loading} />
      ) : (
        <InitialQuestion onOptionChange={handleInitialQuestion} disableOptions={loading} />
      )}
    </Step>
  );
};

export default PersonalizeStep;
